import React, { useState } from 'react';
import { BuildingIcon, SearchIcon, PlusIcon, FilterIcon, MapPinIcon, GlobeIcon, ChevronLeftIcon, ChevronRightIcon, ArrowUpDownIcon, EyeIcon, BarChart3Icon, UsersIcon, DollarSignIcon, PieChartIcon, ClipboardListIcon, BriefcaseIcon, TrendingUpIcon, UserIcon, BellIcon, PhoneIcon, MailIcon, CalendarIcon, ClockIcon, HeartIcon, ThumbsUpIcon, AlertCircleIcon, StarIcon, TagIcon, CheckCircleIcon, MessageSquareIcon, PenIcon, FileTextIcon, HistoryIcon, ArrowRightIcon, ChevronDownIcon, ListIcon, GridIcon, InfoIcon, HelpCircleIcon, RefreshCwIcon, PlusCircleIcon, PercentIcon, LayoutDashboardIcon, ListPlusIcon, ActivityIcon, FolderIcon, SettingsIcon } from 'lucide-react';
import { dealersData } from '../data/dealersData';
import { Link } from 'react-router-dom';
export function DealersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedSize, setSelectedSize] = useState('all');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedLifecycleStage, setSelectedLifecycleStage] = useState('all');
  const [selectedEngagementLevel, setSelectedEngagementLevel] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'card', 'pipeline'
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'active', 'at-risk', 'new', 'dormant'
  const [showReminders, setShowReminders] = useState(true);
  const [showOpportunities, setShowOpportunities] = useState(true);
  const [mainTab, setMainTab] = useState('dealers'); // 'overview', 'dealers', 'opportunities', 'reports', 'settings'
  const itemsPerPage = 10;
  // Get unique regions and countries for filters
  const regions = [...new Set(dealersData.map(dealer => dealer.region))];
  const countries = [...new Set(dealersData.map(dealer => dealer.country))];
  // Filter dealers based on search and filters
  const filteredDealers = dealersData.filter(dealer => {
    const matchesSearch = searchQuery === '' || dealer.name.toLowerCase().includes(searchQuery.toLowerCase()) || dealer.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRegion = selectedRegion === 'all' || dealer.region === selectedRegion;
    const matchesCountry = selectedCountry === 'all' || dealer.country === selectedCountry;
    const matchesStatus = selectedStatus === 'all' || true; // Añadir lógica cuando haya datos de estado
    const matchesSize = selectedSize === 'all' || true; // Añadir lógica cuando haya datos de tamaño
    const matchesTab = activeTab === 'all' || activeTab === 'active' && dealer.status === 'active' || activeTab === 'at-risk' && dealer.conversionRate < 50 || activeTab === 'new' && dealer.recentActivity && dealer.recentActivity.some(a => a.type === 'dealer' && a.description.includes('registered')) || activeTab === 'dormant' && dealer.quotesTrend < 0;
    return matchesSearch && matchesRegion && matchesCountry && matchesStatus && matchesSize && matchesTab;
  });
  // Sort dealers
  const sortedDealers = [...filteredDealers].sort((a, b) => {
    if (sortBy === 'name') {
      return sortDirection === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    } else if (sortBy === 'country') {
      return sortDirection === 'asc' ? a.country.localeCompare(b.country) : b.country.localeCompare(a.country);
    } else if (sortBy === 'region') {
      return sortDirection === 'asc' ? a.region.localeCompare(b.region) : b.region.localeCompare(a.region);
    } else if (sortBy === 'quotes') {
      return sortDirection === 'asc' ? a.totalQuotes - b.totalQuotes : b.totalQuotes - a.totalQuotes;
    } else if (sortBy === 'revenue') {
      return sortDirection === 'asc' ? a.totalRevenue - b.totalRevenue : b.totalRevenue - a.totalRevenue;
    } else if (sortBy === 'engagement') {
      const getEngagementScore = dealer => {
        // Calculate engagement score based on quotes, revenue, and trends
        return dealer.totalQuotes * 0.3 + dealer.totalRevenue / 10000 * 0.3 + dealer.quotesTrend * 0.2 + dealer.revenueTrend * 0.2;
      };
      const scoreA = getEngagementScore(a);
      const scoreB = getEngagementScore(b);
      return sortDirection === 'asc' ? scoreA - scoreB : scoreB - scoreA;
    } else if (sortBy === 'lastActivity') {
      // Sort by most recent activity
      if (!a.recentActivity || !b.recentActivity) return 0;
      const dateA = a.recentActivity[0]?.date ? new Date(a.recentActivity[0].date) : new Date(0);
      const dateB = b.recentActivity[0]?.date ? new Date(b.recentActivity[0].date) : new Date(0);
      return sortDirection === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
    }
    return 0;
  });
  // Pagination
  const totalPages = Math.ceil(sortedDealers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDealers = sortedDealers.slice(startIndex, startIndex + itemsPerPage);
  const handleSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  // Calcular métricas para paneles de resumen
  const totalDealers = dealersData.length;
  const totalRevenue = dealersData.reduce((sum, dealer) => sum + dealer.totalRevenue, 0);
  const totalQuotes = dealersData.reduce((sum, dealer) => sum + dealer.totalQuotes, 0);
  const totalActiveQuotes = dealersData.reduce((sum, dealer) => sum + dealer.activeQuotes, 0);
  const totalCustomers = dealersData.reduce((sum, dealer) => sum + dealer.customers, 0);
  // Distribución por región
  const regionDistribution = regions.map(region => {
    const dealersInRegion = dealersData.filter(dealer => dealer.region === region);
    return {
      region,
      count: dealersInRegion.length,
      percentage: dealersInRegion.length / totalDealers * 100
    };
  }).sort((a, b) => b.count - a.count);
  // Top 3 dealers por ingresos
  const topDealers = [...dealersData].sort((a, b) => b.totalRevenue - a.totalRevenue).slice(0, 3);
  // CRM Metrics
  const activeDealers = dealersData.filter(d => d.status === 'active').length;
  const atRiskDealers = dealersData.filter(d => d.conversionRate < 50).length;
  const newDealers = dealersData.filter(d => d.recentActivity && d.recentActivity.some(a => a.type === 'dealer' && a.description.includes('registered'))).length;
  const dormantDealers = dealersData.filter(d => d.quotesTrend < 0).length;
  // Upcoming interactions & opportunities
  const upcomingInteractions = [{
    id: 1,
    dealerName: "ABE'S IRRIGATION",
    type: 'Call',
    date: 'Tomorrow, 10:00 AM',
    description: 'Quarterly business review',
    priority: 'high'
  }, {
    id: 2,
    dealerName: 'AGROSEEDS LLC',
    type: 'Meeting',
    date: 'Jul 25, 2023',
    description: 'Product training session',
    priority: 'medium'
  }, {
    id: 3,
    dealerName: 'AGRITEC S.A.',
    type: 'Email',
    date: 'Jul 22, 2023',
    description: 'Follow up on quote #AG2023-42',
    priority: 'medium'
  }, {
    id: 4,
    dealerName: 'Adams Mobile Welding LLC',
    type: 'Call',
    date: 'Jul 23, 2023',
    description: 'Discuss new product line',
    priority: 'low'
  }];
  const salesOpportunities = [{
    id: 1,
    dealerName: 'AGROSEEDS LLC',
    value: 120000,
    probability: 75,
    stage: 'Proposal',
    product: 'Center Pivot Package',
    expectedClose: 'Aug 15, 2023'
  }, {
    id: 2,
    dealerName: "ABE'S IRRIGATION",
    value: 85000,
    probability: 60,
    stage: 'Negotiation',
    product: 'Linear Irrigation System',
    expectedClose: 'Aug 30, 2023'
  }, {
    id: 3,
    dealerName: 'AGRO+ SRL',
    value: 165000,
    probability: 40,
    stage: 'Discovery',
    product: 'Complete Farm Solution',
    expectedClose: 'Sep 15, 2023'
  }];
  // Segmentación
  const segments = [{
    name: 'Key Accounts',
    count: Math.round(totalDealers * 0.15),
    color: 'bg-purple-500'
  }, {
    name: 'Growth Potential',
    count: Math.round(totalDealers * 0.25),
    color: 'bg-blue-500'
  }, {
    name: 'Stable',
    count: Math.round(totalDealers * 0.4),
    color: 'bg-green-500'
  }, {
    name: 'At Risk',
    count: Math.round(totalDealers * 0.12),
    color: 'bg-amber-500'
  }, {
    name: 'Declining',
    count: Math.round(totalDealers * 0.08),
    color: 'bg-red-500'
  }];
  // Lifecycle stages
  const lifecycleStages = [{
    name: 'New (0-6 months)',
    count: Math.round(totalDealers * 0.18),
    color: 'bg-emerald-500'
  }, {
    name: 'Growing (6-18 months)',
    count: Math.round(totalDealers * 0.32),
    color: 'bg-blue-500'
  }, {
    name: 'Mature (18+ months)',
    count: Math.round(totalDealers * 0.38),
    color: 'bg-indigo-500'
  }, {
    name: 'Renewal',
    count: Math.round(totalDealers * 0.12),
    color: 'bg-amber-500'
  }];
  // Sample dealer data for table display that matches the image
  const dealerTableData = [{
    id: 1,
    name: 'TEXAS INTERNATIONAL IRRIGATION',
    engagement: 68,
    engagementTrend: '+5%',
    quotes: 32,
    activeQuotes: 18,
    pendingQuotes: 8,
    revenue: 387500,
    revenueTrend: '+22%',
    lastActivity: 'Jul 15, 2023',
    lastActivityDesc: 'New quote #HX6AA6-3 created'
  }, {
    id: 2,
    name: 'UWR',
    engagement: 0,
    engagementStatus: 'Stable',
    quotes: 28,
    activeQuotes: 15,
    pendingQuotes: 7,
    revenue: 325000,
    revenueTrend: '+12%',
    lastActivity: null,
    lastActivityDesc: 'No recent activity'
  }, {
    id: 3,
    name: 'I-BEAM Irrigation',
    engagement: 0,
    engagementStatus: 'Stable',
    quotes: 22,
    activeQuotes: 12,
    pendingQuotes: 5,
    revenue: 267500,
    revenueTrend: '+8%',
    lastActivity: null,
    lastActivityDesc: 'No recent activity'
  }, {
    id: 4,
    name: 'AQUADOC',
    engagement: 0,
    engagementStatus: 'Stable',
    quotes: 18,
    activeQuotes: 10,
    pendingQuotes: 4,
    revenue: 210000,
    revenueTrend: '+15%',
    lastActivity: null,
    lastActivityDesc: 'No recent activity'
  }];
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 to-blue-900" data-id="element-8451">
      <div className="p-6 text-white" data-id="element-8452">
        <h1 className="text-2xl font-bold flex items-center" data-id="element-8453">
          <BuildingIcon className="mr-2" size={24} data-id="element-8454" />
          Dealer Relationship Management
        </h1>
        <p className="text-blue-200 mt-1" data-id="element-8455">
          Manage your dealer relationships, track engagement, and identify
          growth opportunities
        </p>
      </div>
      {/* Main Navigation Tabs */}
      <div className="px-6" data-id="element-8456">
        <div className="flex border-b border-blue-800" data-id="element-8457">
          <MainTab active={mainTab === 'overview'} onClick={() => setMainTab('overview')} icon={<LayoutDashboardIcon size={18} data-id="element-8459" />} label="Overview" data-id="element-8458" />
          <MainTab active={mainTab === 'dealers'} onClick={() => setMainTab('dealers')} icon={<BuildingIcon size={18} data-id="element-8461" />} label="Dealers" data-id="element-8460" />
          <MainTab active={mainTab === 'opportunities'} onClick={() => setMainTab('opportunities')} icon={<TrendingUpIcon size={18} data-id="element-8463" />} label="Opportunities" data-id="element-8462" />
          <MainTab active={mainTab === 'reports'} onClick={() => setMainTab('reports')} icon={<BarChart3Icon size={18} data-id="element-8465" />} label="Reports" data-id="element-8464" />
          <MainTab active={mainTab === 'settings'} onClick={() => setMainTab('settings')} icon={<SettingsIcon size={18} data-id="element-8467" />} label="Settings" data-id="element-8466" />
        </div>
      </div>
      {/* Overview Tab Content */}
      {mainTab === 'overview' && <>
          {/* CRM Navigation Tabs */}
          <div className="px-6 mt-4 mb-4" data-id="element-8468">
            <div className="flex overflow-x-auto space-x-2 pb-2" data-id="element-8469">
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'all' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('all')} data-id="element-8470">
                <BuildingIcon size={16} className="mr-2" data-id="element-8471" />
                All Dealers
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8472">
                  {totalDealers}
                </span>
              </button>
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'active' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('active')} data-id="element-8473">
                <div className="w-2 h-2 rounded-full bg-green-400 mr-2" data-id="element-8474"></div>
                Active
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8475">
                  {activeDealers}
                </span>
              </button>
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'at-risk' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('at-risk')} data-id="element-8476">
                <div className="w-2 h-2 rounded-full bg-amber-400 mr-2" data-id="element-8477"></div>
                At Risk
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8478">
                  {atRiskDealers}
                </span>
              </button>
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'new' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('new')} data-id="element-8479">
                <div className="w-2 h-2 rounded-full bg-purple-400 mr-2" data-id="element-8480"></div>
                New Dealers
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8481">
                  {newDealers}
                </span>
              </button>
              <button className={`px-4 py-2 rounded-lg flex items-center whitespace-nowrap transition-colors ${activeTab === 'dormant' ? 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-md' : 'bg-white/10 text-blue-200 hover:bg-white/15 hover:text-white border border-white/10'}`} onClick={() => setActiveTab('dormant')} data-id="element-8482">
                <div className="w-2 h-2 rounded-full bg-red-400 mr-2" data-id="element-8483"></div>
                Dormant
                <span className="ml-2 bg-white/20 text-white text-xs font-medium rounded-full px-2 py-0.5" data-id="element-8484">
                  {dormantDealers}
                </span>
              </button>
            </div>
          </div>
          {/* CRM Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-6 mb-6" data-id="element-8485">
            {/* Column 1: Performance Metrics */}
            <div className="space-y-4" data-id="element-8486">
              {/* Performance Summary */}
              <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8487">
                <h3 className="text-blue-300 text-sm font-medium mb-3 flex items-center" data-id="element-8488">
                  <BarChart3Icon size={18} className="mr-2 text-blue-300" data-id="element-8489" />
                  Performance Overview
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-8490">
                  <div className="bg-blue-900/30 rounded-lg p-3" data-id="element-8491">
                    <div className="text-blue-300 text-xs mb-1" data-id="element-8492">
                      Total Dealers
                    </div>
                    <div className="text-white text-xl font-bold" data-id="element-8493">
                      {totalDealers}
                    </div>
                    <div className="text-blue-200 text-xs mt-1" data-id="element-8494">
                      <span className="inline-flex items-center" data-id="element-8495">
                        <TrendingUpIcon size={12} className="mr-1 text-green-400" data-id="element-8496" />
                        +{newDealers} new
                      </span>
                    </div>
                  </div>
                  <div className="bg-blue-900/30 rounded-lg p-3" data-id="element-8497">
                    <div className="text-blue-300 text-xs mb-1" data-id="element-8498">
                      Total Revenue
                    </div>
                    <div className="text-white text-xl font-bold" data-id="element-8499">
                      ${(totalRevenue / 1000).toFixed(0)}k
                    </div>
                    <div className="text-blue-200 text-xs mt-1" data-id="element-8500">
                      <span className="inline-flex items-center" data-id="element-8501">
                        <TrendingUpIcon size={12} className="mr-1 text-green-400" data-id="element-8502" />
                        +12% YTD
                      </span>
                    </div>
                  </div>
                  <div className="bg-blue-900/30 rounded-lg p-3" data-id="element-8503">
                    <div className="text-blue-300 text-xs mb-1" data-id="element-8504">
                      Avg. Conversion
                    </div>
                    <div className="text-white text-xl font-bold" data-id="element-8505">62%</div>
                    <div className="text-blue-200 text-xs mt-1" data-id="element-8506">
                      <span className="inline-flex items-center" data-id="element-8507">
                        <TrendingUpIcon size={12} className="mr-1 text-green-400" data-id="element-8508" />
                        +5% vs last quarter
                      </span>
                    </div>
                  </div>
                  <div className="bg-blue-900/30 rounded-lg p-3" data-id="element-8509">
                    <div className="text-blue-300 text-xs mb-1" data-id="element-8510">
                      Retention Rate
                    </div>
                    <div className="text-white text-xl font-bold" data-id="element-8511">85%</div>
                    <div className="text-blue-200 text-xs mt-1" data-id="element-8512">
                      <span className="inline-flex items-center" data-id="element-8513">
                        <TrendingUpIcon size={12} className="mr-1 text-green-400" data-id="element-8514" />
                        +3% vs last year
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Segmentation */}
              <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8515">
                <h3 className="text-blue-300 text-sm font-medium mb-3 flex items-center" data-id="element-8516">
                  <TagIcon size={18} className="mr-2 text-blue-300" data-id="element-8517" />
                  Dealer Segmentation
                </h3>
                <div className="space-y-3" data-id="element-8518">
                  {segments.map(segment => <div key={segment.name} data-id="element-8519">
                      <div className="flex justify-between items-center text-sm mb-1" data-id="element-8520">
                        <span className="text-blue-100" data-id="element-8521">{segment.name}</span>
                        <span className="text-blue-200" data-id="element-8522">
                          {segment.count} dealers
                        </span>
                      </div>
                      <div className="w-full bg-blue-900/50 rounded-full h-2" data-id="element-8523">
                        <div className={`${segment.color} h-2 rounded-full`} style={{
                    width: `${segment.count / totalDealers * 100}%`
                  }} data-id="element-8524"></div>
                      </div>
                    </div>)}
                </div>
                <button className="mt-4 text-xs text-blue-300 hover:text-blue-200 flex items-center" data-id="element-8525">
                  View segmentation details
                  <ChevronRightIcon size={14} className="ml-1" data-id="element-8526" />
                </button>
              </div>
            </div>
            {/* Column 2: Relationship Management */}
            <div className="space-y-4" data-id="element-8527">
              {/* Lifecycle Stages */}
              <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8528">
                <h3 className="text-blue-300 text-sm font-medium mb-3 flex items-center" data-id="element-8529">
                  <RefreshCwIcon size={18} className="mr-2 text-blue-300" data-id="element-8530" />
                  Lifecycle Stages
                </h3>
                <div className="space-y-3" data-id="element-8531">
                  {lifecycleStages.map(stage => <div key={stage.name} data-id="element-8532">
                      <div className="flex justify-between items-center text-sm mb-1" data-id="element-8533">
                        <span className="text-blue-100" data-id="element-8534">{stage.name}</span>
                        <span className="text-blue-200" data-id="element-8535">
                          {stage.count} dealers
                        </span>
                      </div>
                      <div className="w-full bg-blue-900/50 rounded-full h-2" data-id="element-8536">
                        <div className={`${stage.color} h-2 rounded-full`} style={{
                    width: `${stage.count / totalDealers * 100}%`
                  }} data-id="element-8537"></div>
                      </div>
                    </div>)}
                </div>
                <div className="mt-4 text-xs text-blue-200 flex items-center" data-id="element-8538">
                  <InfoIcon size={14} className="mr-1" data-id="element-8539" />
                  Most dealers are in the growth or mature stages
                </div>
              </div>
              {/* Regional Distribution */}
              <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8540">
                <h3 className="text-blue-300 text-sm font-medium mb-3 flex items-center" data-id="element-8541">
                  <GlobeIcon size={18} className="mr-2 text-blue-300" data-id="element-8542" />
                  Regional Distribution
                </h3>
                <div className="space-y-3" data-id="element-8543">
                  {regionDistribution.slice(0, 4).map(region => <div key={region.region} data-id="element-8544">
                      <div className="flex justify-between items-center text-sm mb-1" data-id="element-8545">
                        <span className="text-blue-100" data-id="element-8546">{region.region}</span>
                        <span className="text-blue-200" data-id="element-8547">
                          {region.count} dealers
                        </span>
                      </div>
                      <div className="w-full bg-blue-900/50 rounded-full h-2" data-id="element-8548">
                        <div className="bg-blue-500 h-2 rounded-full" style={{
                    width: `${region.percentage}%`
                  }} data-id="element-8549"></div>
                      </div>
                    </div>)}
                </div>
                <button className="mt-4 text-xs text-blue-300 hover:text-blue-200 flex items-center" data-id="element-8550">
                  View full regional analysis
                  <ChevronRightIcon size={14} className="ml-1" data-id="element-8551" />
                </button>
              </div>
            </div>
            {/* Column 3: Engagement & Opportunities */}
            <div className="space-y-4" data-id="element-8552">
              {/* Upcoming Interactions */}
              {showReminders && <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8553">
                  <div className="flex justify-between items-center mb-3" data-id="element-8554">
                    <h3 className="text-blue-300 text-sm font-medium flex items-center" data-id="element-8555">
                      <CalendarIcon size={18} className="mr-2 text-blue-300" data-id="element-8556" />
                      Upcoming Interactions
                    </h3>
                    <button onClick={() => setShowReminders(false)} className="text-blue-400 hover:text-blue-300" title="Hide panel" data-id="element-8557">
                      <ChevronDownIcon size={18} data-id="element-8558" />
                    </button>
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1" data-id="element-8559">
                    {upcomingInteractions.map(interaction => <div key={interaction.id} className="bg-blue-900/30 rounded-lg p-3" data-id="element-8560">
                        <div className="flex justify-between items-start" data-id="element-8561">
                          <div className="flex items-center" data-id="element-8562">
                            {interaction.type === 'Call' && <PhoneIcon size={14} className="text-blue-400 mr-2" data-id="element-8563" />}
                            {interaction.type === 'Meeting' && <UsersIcon size={14} className="text-green-400 mr-2" data-id="element-8564" />}
                            {interaction.type === 'Email' && <MailIcon size={14} className="text-amber-400 mr-2" data-id="element-8565" />}
                            <span className="text-blue-100 text-sm font-medium" data-id="element-8566">
                              {interaction.type}
                            </span>
                          </div>
                          <div className={`text-xs px-2 py-0.5 rounded-full ${interaction.priority === 'high' ? 'bg-red-900/50 text-red-300' : interaction.priority === 'medium' ? 'bg-amber-900/50 text-amber-300' : 'bg-blue-900/50 text-blue-300'}`} data-id="element-8567">
                            {interaction.priority}
                          </div>
                        </div>
                        <div className="text-white text-sm mt-1" data-id="element-8568">
                          {interaction.dealerName}
                        </div>
                        <div className="text-blue-200 text-xs mt-1" data-id="element-8569">
                          {interaction.description}
                        </div>
                        <div className="flex justify-between items-center mt-2" data-id="element-8570">
                          <div className="text-blue-300 text-xs flex items-center" data-id="element-8571">
                            <ClockIcon size={12} className="mr-1" data-id="element-8572" />
                            {interaction.date}
                          </div>
                          <button className="text-xs text-blue-400 hover:text-blue-300" data-id="element-8573">
                            View details
                          </button>
                        </div>
                      </div>)}
                  </div>
                  <button className="mt-4 w-full py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/50 hover:bg-blue-800/70 rounded-lg flex items-center justify-center" data-id="element-8574">
                    <PlusIcon size={14} className="mr-1" data-id="element-8575" />
                    Schedule New Interaction
                  </button>
                </div>}
              {/* Sales Opportunities */}
              {showOpportunities && <div className="bg-white/10 backdrop-blur-sm border border-blue-800/50 rounded-xl p-5 shadow-lg" data-id="element-8576">
                  <div className="flex justify-between items-center mb-3" data-id="element-8577">
                    <h3 className="text-blue-300 text-sm font-medium flex items-center" data-id="element-8578">
                      <TrendingUpIcon size={18} className="mr-2 text-blue-300" data-id="element-8579" />
                      Sales Opportunities
                    </h3>
                    <button onClick={() => setShowOpportunities(false)} className="text-blue-400 hover:text-blue-300" title="Hide panel" data-id="element-8580">
                      <ChevronDownIcon size={18} data-id="element-8581" />
                    </button>
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1" data-id="element-8582">
                    {salesOpportunities.map(opportunity => <div key={opportunity.id} className="bg-blue-900/30 rounded-lg p-3" data-id="element-8583">
                        <div className="flex justify-between items-start" data-id="element-8584">
                          <div className="text-white text-sm font-medium" data-id="element-8585">
                            {opportunity.dealerName}
                          </div>
                          <div className="text-green-300 text-sm font-medium" data-id="element-8586">
                            ${opportunity.value.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-blue-200 text-xs mt-1" data-id="element-8587">
                          {opportunity.product}
                        </div>
                        <div className="mt-2 flex items-center" data-id="element-8588">
                          <div className="w-full bg-blue-900/50 rounded-full h-1.5 mr-2" data-id="element-8589">
                            <div className={`${opportunity.probability >= 70 ? 'bg-green-500' : opportunity.probability >= 40 ? 'bg-amber-500' : 'bg-blue-500'} h-1.5 rounded-full`} style={{
                      width: `${opportunity.probability}%`
                    }} data-id="element-8590"></div>
                          </div>
                          <div className="text-blue-300 text-xs whitespace-nowrap" data-id="element-8591">
                            {opportunity.probability}%
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-2" data-id="element-8592">
                          <div className="text-xs px-2 py-0.5 rounded-full bg-blue-800/70 text-blue-300" data-id="element-8593">
                            {opportunity.stage}
                          </div>
                          <div className="text-blue-300 text-xs flex items-center" data-id="element-8594">
                            <CalendarIcon size={12} className="mr-1" data-id="element-8595" />
                            {opportunity.expectedClose}
                          </div>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-4 flex space-x-2" data-id="element-8596">
                    <button className="flex-1 py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/50 hover:bg-blue-800/70 rounded-lg flex items-center justify-center" data-id="element-8597">
                      <PlusIcon size={14} className="mr-1" data-id="element-8598" />
                      Add Opportunity
                    </button>
                    <button className="flex-1 py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/50 hover:bg-blue-800/70 rounded-lg flex items-center justify-center" data-id="element-8599">
                      View Pipeline
                    </button>
                  </div>
                </div>}
              {/* Hidden panels control */}
              <div className="flex space-x-2" data-id="element-8600">
                {!showReminders && <button onClick={() => setShowReminders(true)} className="flex-1 py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/30 hover:bg-blue-800/50 rounded-lg flex items-center justify-center" data-id="element-8601">
                    <CalendarIcon size={14} className="mr-1" data-id="element-8602" />
                    Show Interactions
                  </button>}
                {!showOpportunities && <button onClick={() => setShowOpportunities(true)} className="flex-1 py-2 text-sm text-blue-300 hover:text-blue-200 bg-blue-800/30 hover:bg-blue-800/50 rounded-lg flex items-center justify-center" data-id="element-8603">
                    <TrendingUpIcon size={14} className="mr-1" data-id="element-8604" />
                    Show Opportunities
                  </button>}
              </div>
            </div>
          </div>
        </>}
      {/* Dealers Tab Content */}
      {mainTab === 'dealers' && <>
          {/* Filters */}
          {showFilters && <div className="mx-6 mt-4 mb-4 p-5 bg-gradient-to-r from-blue-900/70 to-indigo-900/70 border border-blue-700 rounded-xl shadow-lg" data-id="element-8605">
              <h3 className="text-white text-lg font-medium mb-4 flex items-center" data-id="element-8606">
                <FilterIcon size={18} className="mr-2 text-blue-300" data-id="element-8607" />
                Advanced CRM Filters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-8608">
                <div data-id="element-8609">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8610">
                    Region
                  </label>
                  <div className="relative" data-id="element-8611">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedRegion} onChange={e => setSelectedRegion(e.target.value)} data-id="element-8612">
                      <option value="all" data-id="element-8613">All Regions</option>
                      {regions.map(region => <option key={region} value={region} data-id="element-8614">
                          {region}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8615">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8616">
                        <polyline points="6 9 12 15 18 9" data-id="element-8617"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8618">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8619">
                    Country
                  </label>
                  <div className="relative" data-id="element-8620">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)} data-id="element-8621">
                      <option value="all" data-id="element-8622">All Countries</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-8623">
                          {country}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8624">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8625">
                        <polyline points="6 9 12 15 18 9" data-id="element-8626"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8627">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8628">
                    Dealer Segment
                  </label>
                  <div className="relative" data-id="element-8629">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedSegment} onChange={e => setSelectedSegment(e.target.value)} data-id="element-8630">
                      <option value="all" data-id="element-8631">All Segments</option>
                      {segments.map(segment => <option key={segment.name} value={segment.name.toLowerCase().replace(' ', '-')} data-id="element-8632">
                          {segment.name}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8633">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8634">
                        <polyline points="6 9 12 15 18 9" data-id="element-8635"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8636">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8637">
                    Lifecycle Stage
                  </label>
                  <div className="relative" data-id="element-8638">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedLifecycleStage} onChange={e => setSelectedLifecycleStage(e.target.value)} data-id="element-8639">
                      <option value="all" data-id="element-8640">All Lifecycle Stages</option>
                      {lifecycleStages.map(stage => <option key={stage.name} value={stage.name.toLowerCase().replace(/[()]/g, '').replace(/ /g, '-')} data-id="element-8641">
                          {stage.name}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8642">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8643">
                        <polyline points="6 9 12 15 18 9" data-id="element-8644"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8645">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8646">
                    Engagement Level
                  </label>
                  <div className="relative" data-id="element-8647">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedEngagementLevel} onChange={e => setSelectedEngagementLevel(e.target.value)} data-id="element-8648">
                      <option value="all" data-id="element-8649">All Engagement Levels</option>
                      <option value="high" data-id="element-8650">High Engagement</option>
                      <option value="medium" data-id="element-8651">Medium Engagement</option>
                      <option value="low" data-id="element-8652">Low Engagement</option>
                      <option value="inactive" data-id="element-8653">Inactive</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8654">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8655">
                        <polyline points="6 9 12 15 18 9" data-id="element-8656"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8657">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8658">
                    Status
                  </label>
                  <div className="relative" data-id="element-8659">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-8660">
                      <option value="all" data-id="element-8661">All Statuses</option>
                      <option value="active" data-id="element-8662">Active</option>
                      <option value="pending" data-id="element-8663">Pending Approval</option>
                      <option value="inactive" data-id="element-8664">Inactive</option>
                      <option value="suspended" data-id="element-8665">Suspended</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8666">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8667">
                        <polyline points="6 9 12 15 18 9" data-id="element-8668"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8669">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8670">
                    Dealer Size
                  </label>
                  <div className="relative" data-id="element-8671">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" value={selectedSize} onChange={e => setSelectedSize(e.target.value)} data-id="element-8672">
                      <option value="all" data-id="element-8673">All Sizes</option>
                      <option value="small" data-id="element-8674">Small (1-50 customers)</option>
                      <option value="medium" data-id="element-8675">Medium (51-200 customers)</option>
                      <option value="large" data-id="element-8676">Large (201+ customers)</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8677">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8678">
                        <polyline points="6 9 12 15 18 9" data-id="element-8679"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
                <div data-id="element-8680">
                  <label className="block text-sm font-medium text-blue-300 mb-2" data-id="element-8681">
                    Last Interaction
                  </label>
                  <div className="relative" data-id="element-8682">
                    <select className="w-full appearance-none bg-blue-800/60 border border-blue-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm" data-id="element-8683">
                      <option value="all" data-id="element-8684">All Time</option>
                      <option value="7days" data-id="element-8685">Last 7 days</option>
                      <option value="30days" data-id="element-8686">Last 30 days</option>
                      <option value="90days" data-id="element-8687">Last 90 days</option>
                      <option value="overdue" data-id="element-8688">Overdue (90+ days)</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-8689">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8690">
                        <polyline points="6 9 12 15 18 9" data-id="element-8691"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-8692">
                <button className="px-4 py-2.5 bg-blue-800 border border-blue-700 rounded-lg text-blue-200 hover:bg-blue-700 transition-colors" onClick={() => {
            setSelectedRegion('all');
            setSelectedCountry('all');
            setSelectedStatus('all');
            setSelectedSize('all');
            setSelectedSegment('all');
            setSelectedLifecycleStage('all');
            setSelectedEngagementLevel('all');
          }} data-id="element-8693">
                  Reset Filters
                </button>
                <button className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md" onClick={() => setShowFilters(false)} data-id="element-8694">
                  Apply Filters
                </button>
              </div>
            </div>}
          {/* Action bar */}
          <div className="px-6 py-4 flex flex-wrap justify-between items-center gap-4" data-id="element-8695">
            <div className="flex items-center gap-3" data-id="element-8696">
              <div className="relative" data-id="element-8697">
                <input type="text" placeholder="Search dealers..." className="pl-10 pr-4 py-2.5 rounded-lg border border-blue-700 bg-blue-900/70 text-white w-72 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-md" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-8698" />
                <SearchIcon className="absolute left-3 top-3 text-blue-400" size={18} data-id="element-8699" />
              </div>
              <div className="flex rounded-lg overflow-hidden border border-blue-700 shadow-md" data-id="element-8700">
                <button className={`px-3 py-2.5 flex items-center ${viewMode === 'table' ? 'bg-blue-700 text-white' : 'bg-blue-900/70 text-blue-300 hover:bg-blue-800'}`} onClick={() => setViewMode('table')} data-id="element-8701">
                  <ListIcon size={16} className="mr-1" data-id="element-8702" />
                  Table
                </button>
                <button className={`px-3 py-2.5 flex items-center ${viewMode === 'card' ? 'bg-blue-700 text-white' : 'bg-blue-900/70 text-blue-300 hover:bg-blue-800'}`} onClick={() => setViewMode('card')} data-id="element-8703">
                  <GridIcon size={16} className="mr-1" data-id="element-8704" />
                  Cards
                </button>
                <button className={`px-3 py-2.5 flex items-center ${viewMode === 'pipeline' ? 'bg-blue-700 text-white' : 'bg-blue-900/70 text-blue-300 hover:bg-blue-800'}`} onClick={() => setViewMode('pipeline')} data-id="element-8705">
                  <TrendingUpIcon size={16} className="mr-1" data-id="element-8706" />
                  Pipeline
                </button>
              </div>
            </div>
            <div className="flex gap-3" data-id="element-8707">
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center px-4 py-2.5 rounded-lg border shadow-md ${showFilters ? 'bg-blue-600 text-white border-blue-500' : 'bg-blue-900/70 text-blue-300 border-blue-700 hover:bg-blue-800'} transition-colors`} data-id="element-8708">
                <FilterIcon size={16} className="mr-2" data-id="element-8709" />
                Filters
                {(selectedRegion !== 'all' || selectedCountry !== 'all' || selectedStatus !== 'all' || selectedSize !== 'all' || selectedSegment !== 'all' || selectedLifecycleStage !== 'all' || selectedEngagementLevel !== 'all') && <span className="ml-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-id="element-8710">
                    {[selectedRegion, selectedCountry, selectedStatus, selectedSize, selectedSegment, selectedLifecycleStage, selectedEngagementLevel].filter(f => f !== 'all').length}
                  </span>}
              </button>
              <button className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg transition-colors shadow-md" data-id="element-8711">
                <PlusIcon size={18} className="mr-2" data-id="element-8712" />
                Add Dealer
              </button>
            </div>
          </div>
          {/* Dealers display based on view mode */}
          <div className="flex-1 px-6 pb-6 overflow-auto" data-id="element-8713">
            {viewMode === 'table' && <div className="bg-white/5 backdrop-blur-sm border border-blue-800/40 rounded-xl shadow-xl overflow-hidden" data-id="element-8714">
                <div className="overflow-x-auto" data-id="element-8715">
                  <table className="w-full" data-id="element-8716">
                    <thead data-id="element-8717">
                      <tr className="border-b border-blue-800/40" data-id="element-8718">
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8719">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('name')} data-id="element-8720">
                            Dealer Name
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'name' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8721" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8722">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('engagement')} data-id="element-8723">
                            Engagement
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'engagement' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8724" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8725">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('quotes')} data-id="element-8726">
                            Quotes
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'quotes' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8727" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8728">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('revenue')} data-id="element-8729">
                            Revenue
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'revenue' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8730" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8731">
                          <button className="flex items-center focus:outline-none" onClick={() => handleSort('lastActivity')} data-id="element-8732">
                            Last Activity
                            <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'lastActivity' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-8733" />
                          </button>
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-8734">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-blue-800/30" data-id="element-8735">
                      {dealerTableData.map(dealer => <tr key={dealer.id} className="hover:bg-blue-800/20 transition-colors" data-id="element-8736">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8737">
                            <div className="flex items-center" data-id="element-8738">
                              <div className="flex-shrink-0 h-10 w-10 bg-blue-900/30 rounded-full flex items-center justify-center border border-blue-700/30" data-id="element-8739">
                                <BuildingIcon className="h-5 w-5 text-blue-500" aria-hidden="true" data-id="element-8740" />
                              </div>
                              <div className="ml-4" data-id="element-8741">
                                <div className="text-sm font-medium text-white" data-id="element-8742">
                                  {dealer.name}
                                </div>
                                <div className="text-xs text-blue-300" data-id="element-8743">
                                  ID: {dealer.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8744">
                            <div className="flex items-center" data-id="element-8745">
                              {dealer.engagement > 0 ? <>
                                  <div className="relative h-2 w-24 bg-blue-900/50 rounded mr-2" data-id="element-8746">
                                    <div className="absolute top-0 left-0 h-2 rounded bg-blue-500" style={{
                            width: `${dealer.engagement}%`
                          }} data-id="element-8747"></div>
                                  </div>
                                  <span className="text-sm text-white" data-id="element-8748">
                                    {dealer.engagement}%
                                  </span>
                                </> : <span className="text-sm text-blue-300" data-id="element-8749">
                                  {dealer.engagementStatus || '0%'}
                                </span>}
                            </div>
                            {dealer.engagementTrend && <div className="text-xs text-green-400 mt-1 flex items-center" data-id="element-8750">
                                <TrendingUpIcon size={12} className="mr-1" data-id="element-8751" />
                                {dealer.engagementTrend}
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8752">
                            <div className="flex items-center text-sm text-white" data-id="element-8753">
                              <BarChart3Icon size={16} className="mr-1 text-blue-500" data-id="element-8754" />
                              {dealer.quotes}
                            </div>
                            <div className="text-xs text-blue-300 mt-1" data-id="element-8755">
                              <span className="inline-flex items-center" data-id="element-8756">
                                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1" data-id="element-8757"></span>
                                Active: {dealer.activeQuotes}
                              </span>
                              {' · '}
                              <span className="inline-flex items-center" data-id="element-8758">
                                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1" data-id="element-8759"></span>
                                Pending: {dealer.pendingQuotes}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8760">
                            <div className="text-sm text-white" data-id="element-8761">
                              ${dealer.revenue.toLocaleString()}
                            </div>
                            <div className="text-xs text-green-400 mt-1 flex items-center" data-id="element-8762">
                              <TrendingUpIcon size={12} className="mr-1" data-id="element-8763" />
                              {dealer.revenueTrend}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-8764">
                            {dealer.lastActivity ? <div data-id="element-8765">
                                <div className="text-sm text-white" data-id="element-8766">
                                  {dealer.lastActivity}
                                </div>
                                <div className="text-xs text-blue-300 mt-1" data-id="element-8767">
                                  {dealer.lastActivityDesc}
                                </div>
                              </div> : <div className="text-sm text-blue-300" data-id="element-8768">
                                {dealer.lastActivityDesc}
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8769">
                            <div className="flex justify-end space-x-2" data-id="element-8770">
                              <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-1.5 rounded-md transition-colors" data-id="element-8771">
                                <PhoneIcon size={16} data-id="element-8772" />
                              </button>
                              <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-1.5 rounded-md transition-colors" data-id="element-8773">
                                <MailIcon size={16} data-id="element-8774" />
                              </button>
                              <Link to={`/dealers/${dealer.id}`} className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 px-3 py-1.5 rounded-md transition-colors inline-flex items-center" data-id="element-8775">
                                <EyeIcon size={16} className="mr-1" data-id="element-8776" />
                                View
                              </Link>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="bg-blue-900/20 px-6 py-3 flex items-center justify-between border-t border-blue-800/30" data-id="element-8777">
                  <div className="flex-1 flex justify-between items-center" data-id="element-8778">
                    <div data-id="element-8779">
                      <p className="text-sm text-blue-300" data-id="element-8780">
                        Showing{' '}
                        <span className="font-medium text-white" data-id="element-8781">1</span> to{' '}
                        <span className="font-medium text-white" data-id="element-8782">
                          {dealerTableData.length}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium text-white" data-id="element-8783">
                          {totalDealers}
                        </span>{' '}
                        dealers
                      </p>
                    </div>
                    <div data-id="element-8784">
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-8785">
                        <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-blue-700 bg-blue-900/20 text-sm font-medium text-blue-300 hover:bg-blue-800/30" data-id="element-8786">
                          <span className="sr-only" data-id="element-8787">Previous</span>
                          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-8788" />
                        </button>
                        <button className="relative inline-flex items-center px-4 py-2 border border-blue-700 bg-blue-700 text-sm font-medium text-white" data-id="element-8789">
                          1
                        </button>
                        <button className="relative inline-flex items-center px-4 py-2 border border-blue-700 bg-blue-900/20 text-sm font-medium text-blue-300 hover:bg-blue-800/30" data-id="element-8790">
                          2
                        </button>
                        <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-blue-700 bg-blue-900/20 text-sm font-medium text-blue-300 hover:bg-blue-800/30" data-id="element-8791">
                          <span className="sr-only" data-id="element-8792">Next</span>
                          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-8793" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>}
            {viewMode === 'card' && <div data-id="element-8794">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-8795">
                  {paginatedDealers.map(dealer => <div key={dealer.id} className="bg-white/5 backdrop-blur-sm border border-blue-800/40 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow" data-id="element-8796">
                      <div className="p-5 border-b border-blue-800/30" data-id="element-8797">
                        <div className="flex justify-between items-start" data-id="element-8798">
                          <div className="flex items-center" data-id="element-8799">
                            <div className="h-12 w-12 bg-blue-900/30 rounded-full flex items-center justify-center border border-blue-700/30" data-id="element-8800">
                              <BuildingIcon className="h-6 w-6 text-blue-500" data-id="element-8801" />
                            </div>
                            <div className="ml-3" data-id="element-8802">
                              <h3 className="text-lg font-medium text-white" data-id="element-8803">
                                {dealer.name}
                              </h3>
                              <div className="flex items-center text-sm text-blue-300" data-id="element-8804">
                                <MapPinIcon size={14} className="mr-1" data-id="element-8805" />
                                {dealer.country}
                              </div>
                            </div>
                          </div>
                          {dealer.status === 'active' && <span className="bg-green-900/30 text-green-400 border border-green-700/30 text-xs px-2 py-1 rounded-full" data-id="element-8806">
                              Active
                            </span>}
                          {dealer.status === 'pending' && <span className="bg-amber-900/30 text-amber-400 border border-amber-700/30 text-xs px-2 py-1 rounded-full" data-id="element-8807">
                              Pending
                            </span>}
                          {dealer.status === 'inactive' && <span className="bg-blue-900/30 text-blue-400 border border-blue-700/30 text-xs px-2 py-1 rounded-full" data-id="element-8808">
                              Inactive
                            </span>}
                        </div>
                      </div>
                      <div className="p-5" data-id="element-8809">
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-8810">
                          <div data-id="element-8811">
                            <div className="text-xs text-blue-300 mb-1" data-id="element-8812">
                              Total Revenue
                            </div>
                            <div className="text-lg font-medium text-white" data-id="element-8813">
                              ${dealer.totalRevenue.toLocaleString()}
                            </div>
                            <div className="text-xs text-blue-300 mt-1 flex items-center" data-id="element-8814">
                              {dealer.revenueTrend > 0 ? <span className="text-green-400 flex items-center" data-id="element-8815">
                                  <TrendingUpIcon size={12} className="mr-1" data-id="element-8816" />+
                                  {dealer.revenueTrend}%
                                </span> : dealer.revenueTrend < 0 ? <span className="text-red-400 flex items-center" data-id="element-8817">
                                  <TrendingUpIcon size={12} className="mr-1 rotate-180" data-id="element-8818" />
                                  {dealer.revenueTrend}%
                                </span> : <span className="text-blue-300" data-id="element-8819">Stable</span>}
                            </div>
                          </div>
                          <div data-id="element-8820">
                            <div className="text-xs text-blue-300 mb-1" data-id="element-8821">
                              Quotes
                            </div>
                            <div className="text-lg font-medium text-white" data-id="element-8822">
                              {dealer.totalQuotes}
                            </div>
                            <div className="text-xs text-blue-300 mt-1" data-id="element-8823">
                              Active: {dealer.activeQuotes} / Pending:{' '}
                              {dealer.pendingQuotes || 0}
                            </div>
                          </div>
                        </div>
                        <div className="mb-4" data-id="element-8824">
                          <div className="flex justify-between items-center mb-1" data-id="element-8825">
                            <div className="text-xs text-blue-300" data-id="element-8826">
                              Engagement
                            </div>
                            <div className="text-xs font-medium text-white" data-id="element-8827">
                              {dealer.conversionRate || 0}%
                            </div>
                          </div>
                          <div className="w-full h-2 bg-blue-900/50 rounded-full overflow-hidden" data-id="element-8828">
                            <div className={`h-2 ${dealer.conversionRate >= 70 ? 'bg-green-500' : dealer.conversionRate >= 50 ? 'bg-blue-500' : dealer.conversionRate >= 30 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                      width: `${dealer.conversionRate || 0}%`
                    }} data-id="element-8829"></div>
                          </div>
                        </div>
                        {dealer.recentActivity && dealer.recentActivity[0] && <div className="mb-4" data-id="element-8830">
                            <div className="text-xs text-blue-300 mb-1" data-id="element-8831">
                              Last Activity
                            </div>
                            <div className="text-sm text-white" data-id="element-8832">
                              {dealer.recentActivity[0].description}
                            </div>
                            <div className="text-xs text-blue-300 mt-1" data-id="element-8833">
                              {dealer.recentActivity[0].date}
                            </div>
                          </div>}
                        <div className="flex justify-between items-center mt-4" data-id="element-8834">
                          <div className="flex space-x-2" data-id="element-8835">
                            <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-2 rounded-md transition-colors" data-id="element-8836">
                              <PhoneIcon size={16} data-id="element-8837" />
                            </button>
                            <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-2 rounded-md transition-colors" data-id="element-8838">
                              <MailIcon size={16} data-id="element-8839" />
                            </button>
                            <button className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-2 rounded-md transition-colors" data-id="element-8840">
                              <CalendarIcon size={16} data-id="element-8841" />
                            </button>
                          </div>
                          <Link to={`/dealers/${dealer.id}`} className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 px-3 py-2 rounded-md transition-colors inline-flex items-center" data-id="element-8842">
                            <EyeIcon size={16} className="mr-1" data-id="element-8843" />
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>)}
                </div>
                {/* Pagination */}
                {totalPages > 1 && <div className="flex justify-between items-center py-4 mt-4 bg-white/5 backdrop-blur-sm rounded-xl px-6 shadow-md" data-id="element-8844">
                    <button className="flex items-center text-blue-300 hover:text-blue-200 disabled:text-blue-500/50" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} data-id="element-8845">
                      <ChevronLeftIcon size={16} className="mr-1" data-id="element-8846" />
                      Previous
                    </button>
                    <div className="flex items-center gap-1" data-id="element-8847">
                      {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} className={`w-8 h-8 rounded-full flex items-center justify-center ${currentPage === page ? 'bg-blue-600 text-white' : 'text-blue-300 hover:bg-blue-800/50'}`} onClick={() => setCurrentPage(page)} data-id="element-8848">
                          {page}
                        </button>)}
                    </div>
                    <button className="flex items-center text-blue-300 hover:text-blue-200 disabled:text-blue-500/50" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} data-id="element-8849">
                      Next
                      <ChevronRightIcon size={16} className="ml-1" data-id="element-8850" />
                    </button>
                  </div>}
              </div>}
            {viewMode === 'pipeline' && <div className="bg-white/5 backdrop-blur-sm border border-blue-800/40 rounded-xl shadow-xl overflow-hidden p-6" data-id="element-8851">
                <h3 className="text-lg font-medium text-white mb-4" data-id="element-8852">
                  Dealer Pipeline View
                </h3>
                <p className="text-blue-300 mb-6" data-id="element-8853">
                  Visualize your dealer relationships across different stages of
                  the lifecycle
                </p>
                <div className="flex overflow-x-auto pb-4 space-x-4" data-id="element-8854">
                  {/* New Dealers Column */}
                  <div className="min-w-[300px] bg-purple-900/20 rounded-lg border border-purple-700/30 flex flex-col" data-id="element-8855">
                    <div className="p-4 border-b border-purple-700/30 bg-purple-900/30" data-id="element-8856">
                      <h4 className="font-medium text-purple-300 flex items-center" data-id="element-8857">
                        <PlusCircleIcon size={18} className="mr-2" data-id="element-8858" />
                        New Dealers
                        <span className="ml-2 bg-purple-900/50 text-purple-300 border border-purple-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8859">
                          {newDealers}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8860">
                      {dealersData.filter(d => d.recentActivity && d.recentActivity.some(a => a.type === 'dealer' && a.description.includes('registered'))).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-purple-700/30" data-id="element-8861">
                            <div className="font-medium text-white" data-id="element-8862">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8863">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8864" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 text-xs text-purple-300" data-id="element-8865">
                              New dealer onboarding in progress
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8866">
                              <div className="text-xs text-blue-300" data-id="element-8867">
                                {dealer.recentActivity && dealer.recentActivity[0]?.date}
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-purple-400 hover:text-purple-300 text-xs" data-id="element-8868">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* Growing Dealers Column */}
                  <div className="min-w-[300px] bg-blue-900/20 rounded-lg border border-blue-700/30 flex flex-col" data-id="element-8869">
                    <div className="p-4 border-b border-blue-700/30 bg-blue-900/30" data-id="element-8870">
                      <h4 className="font-medium text-blue-300 flex items-center" data-id="element-8871">
                        <TrendingUpIcon size={18} className="mr-2" data-id="element-8872" />
                        Growing
                        <span className="ml-2 bg-blue-900/50 text-blue-300 border border-blue-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8873">
                          {dealersData.filter(d => d.quotesTrend > 10).length}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8874">
                      {dealersData.filter(d => d.quotesTrend > 10).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-blue-700/30" data-id="element-8875">
                            <div className="font-medium text-white" data-id="element-8876">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8877">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8878" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 flex items-center text-sm text-blue-300" data-id="element-8879">
                              <TrendingUpIcon size={14} className="mr-1" data-id="element-8880" />
                              {dealer.quotesTrend}% growth in quotes
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8881">
                              <div className="text-xs text-blue-300" data-id="element-8882">
                                ${dealer.totalRevenue.toLocaleString()} revenue
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-blue-400 hover:text-blue-300 text-xs" data-id="element-8883">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* Stable Dealers Column */}
                  <div className="min-w-[300px] bg-green-900/20 rounded-lg border border-green-700/30 flex flex-col" data-id="element-8884">
                    <div className="p-4 border-b border-green-700/30 bg-green-900/30" data-id="element-8885">
                      <h4 className="font-medium text-green-300 flex items-center" data-id="element-8886">
                        <CheckCircleIcon size={18} className="mr-2" data-id="element-8887" />
                        Stable
                        <span className="ml-2 bg-green-900/50 text-green-300 border border-green-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8888">
                          {dealersData.filter(d => d.quotesTrend >= 0 && d.quotesTrend <= 10).length}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8889">
                      {dealersData.filter(d => d.quotesTrend >= 0 && d.quotesTrend <= 10).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-green-700/30" data-id="element-8890">
                            <div className="font-medium text-white" data-id="element-8891">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8892">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8893" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 text-xs text-green-300" data-id="element-8894">
                              Consistent performance
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8895">
                              <div className="text-xs text-blue-300" data-id="element-8896">
                                {dealer.totalQuotes} total quotes
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-green-400 hover:text-green-300 text-xs" data-id="element-8897">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* At Risk Dealers Column */}
                  <div className="min-w-[300px] bg-amber-900/20 rounded-lg border border-amber-700/30 flex flex-col" data-id="element-8898">
                    <div className="p-4 border-b border-amber-700/30 bg-amber-900/30" data-id="element-8899">
                      <h4 className="font-medium text-amber-300 flex items-center" data-id="element-8900">
                        <AlertCircleIcon size={18} className="mr-2" data-id="element-8901" />
                        At Risk
                        <span className="ml-2 bg-amber-900/50 text-amber-300 border border-amber-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8902">
                          {atRiskDealers}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8903">
                      {dealersData.filter(d => d.conversionRate < 50).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-amber-700/30" data-id="element-8904">
                            <div className="font-medium text-white" data-id="element-8905">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8906">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8907" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 text-xs text-amber-300 flex items-center" data-id="element-8908">
                              <AlertCircleIcon size={14} className="mr-1" data-id="element-8909" />
                              Low conversion rate: {dealer.conversionRate || 0}%
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8910">
                              <div className="text-xs text-blue-300" data-id="element-8911">
                                Needs attention
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-amber-400 hover:text-amber-300 text-xs" data-id="element-8912">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                  {/* Dormant Dealers Column */}
                  <div className="min-w-[300px] bg-red-900/20 rounded-lg border border-red-700/30 flex flex-col" data-id="element-8913">
                    <div className="p-4 border-b border-red-700/30 bg-red-900/30" data-id="element-8914">
                      <h4 className="font-medium text-red-300 flex items-center" data-id="element-8915">
                        <ClockIcon size={18} className="mr-2" data-id="element-8916" />
                        Dormant
                        <span className="ml-2 bg-red-900/50 text-red-300 border border-red-700/30 text-xs rounded-full px-2 py-0.5" data-id="element-8917">
                          {dormantDealers}
                        </span>
                      </h4>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto max-h-[500px]" data-id="element-8918">
                      {dealersData.filter(d => d.quotesTrend < 0).slice(0, 4).map(dealer => <div key={dealer.id} className="mb-3 bg-white/5 backdrop-blur-sm p-3 rounded-lg shadow-sm border border-red-700/30" data-id="element-8919">
                            <div className="font-medium text-white" data-id="element-8920">
                              {dealer.name}
                            </div>
                            <div className="text-sm text-blue-300 mt-1 flex items-center" data-id="element-8921">
                              <MapPinIcon size={14} className="mr-1" data-id="element-8922" />
                              {dealer.country}
                            </div>
                            <div className="mt-2 text-xs text-red-300 flex items-center" data-id="element-8923">
                              <TrendingUpIcon size={14} className="mr-1 rotate-180" data-id="element-8924" />
                              {Math.abs(dealer.quotesTrend)}% decline in
                              activity
                            </div>
                            <div className="mt-2 flex justify-between items-center" data-id="element-8925">
                              <div className="text-xs text-blue-300" data-id="element-8926">
                                Last active:{' '}
                                {dealer.recentActivity && dealer.recentActivity[0]?.date}
                              </div>
                              <Link to={`/dealers/${dealer.id}`} className="text-red-400 hover:text-red-300 text-xs" data-id="element-8927">
                                View
                              </Link>
                            </div>
                          </div>)}
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </>}
      {/* Other tabs content */}
      {(mainTab === 'opportunities' || mainTab === 'reports' || mainTab === 'settings') && <div className="flex items-center justify-center h-64 p-6" data-id="element-8928">
          <div className="text-center text-white" data-id="element-8929">
            <h2 className="text-xl font-medium mb-2" data-id="element-8930">
              {mainTab.charAt(0).toUpperCase() + mainTab.slice(1)} Module
            </h2>
            <p className="text-blue-300" data-id="element-8931">This section is under development</p>
          </div>
        </div>}
    </div>;
}
// Tab component for main navigation
function MainTab({
  active,
  onClick,
  icon,
  label
}) {
  return <button className={`px-6 py-3 font-medium transition-colors flex items-center ${active ? 'text-white border-b-2 border-white' : 'text-blue-300 hover:text-white'}`} onClick={onClick} data-id="element-8932">
      <span className="mr-2" data-id="element-8933">{icon}</span>
      {label}
    </button>;
}